import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { bannerTypesService } from '@/services/inventory';
import type { BannerSurface, BannerType, BannerTypeInput } from '@/types/inventory';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY: BannerTypeInput = { name: '', aspectRatio: '', width: 0, height: 0, display: '', showsOn: null };

const SURFACE_LABELS: Record<BannerSurface, string> = {
  LISTING_CARD: 'Listing card',
  PRODUCT_PAGE: 'Product page',
  CATEGORY: 'Category',
};

/**
 * Manage the tenant's banner types (placements) — name, aspect ratio, size,
 * display description. Add as many as needed; reorder; edit; delete (blocked
 * while banners still use a type). Shared across all items.
 */
export function BannerTypesManager({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const typesQuery = useQuery({
    queryKey: ['inventory', 'banner-types'],
    queryFn: () => bannerTypesService.list(),
    enabled: open,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BannerTypeInput>(EMPTY);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setForm(EMPTY);
    }
  }, [open]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory', 'banner-types'] });

  const save = useMutation({
    mutationFn: () =>
      editingId
        ? bannerTypesService.update(editingId, normalize(form))
        : bannerTypesService.create(normalize(form)),
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Type updated' : 'Type added');
      setEditingId(null);
      setForm(EMPTY);
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not save'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => bannerTypesService.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success('Type deleted');
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not delete'),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => bannerTypesService.reorder(ids),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not reorder'),
  });

  const types = typesQuery.data ?? [];

  const startEdit = (t: BannerType) => {
    setEditingId(t.id);
    setForm({
      name: t.name,
      aspectRatio: t.aspectRatio,
      width: t.width,
      height: t.height,
      display: t.display ?? '',
      showsOn: t.showsOn ?? null,
    });
  };

  const move = (idx: number, dir: -1 | 1) => {
    const ids = types.map((t) => t.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    reorder.mutate(ids);
  };

  const canSave =
    form.name.trim().length > 0 &&
    /^\d{1,3}:\d{1,3}$/.test(form.aspectRatio.trim()) &&
    form.width > 0 &&
    form.height > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Banner types</DialogTitle>
          <DialogDescription>
            Define the placements your storefront uses — name, aspect ratio, size and a short
            description. Items attach banner images to these types.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Existing types */}
          {typesQuery.isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : types.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No types yet — add your first below.</p>
          ) : (
            <ul className="divide-y divide-border/60 rounded-md border border-border/60">
              {types.map((t, idx) => (
                <li key={t.id} className="flex items-center gap-2 px-3 py-2">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      disabled={idx === 0 || reorder.isPending}
                      onClick={() => move(idx, -1)}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === types.length - 1 || reorder.isPending}
                      onClick={() => move(idx, 1)}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-medium text-foreground">{t.name}</span>
                      {t.showsOn ? (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                          {SURFACE_LABELS[t.showsOn]}
                        </span>
                      ) : null}
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {t.aspectRatio} · {t.width}×{t.height}
                      {t.display ? ` · ${t.display}` : ''}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit" onClick={() => startEdit(t)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    title="Delete"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(t.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {/* Add / edit form */}
          <div className="rounded-md border border-border/60 bg-muted/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[12px] font-medium text-foreground">
                {editingId ? 'Edit type' : 'Add type'}
              </span>
              {editingId && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setEditingId(null);
                    setForm(EMPTY);
                  }}
                >
                  <X className="h-3 w-3" /> Cancel edit
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Name" className="sm:col-span-2">
                <Input
                  value={form.name}
                  placeholder="Feature"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </Field>
              <Field label="Aspect (w:h)">
                <Input
                  value={form.aspectRatio}
                  placeholder="3:1"
                  className="font-mono"
                  onChange={(e) => setForm((f) => ({ ...f, aspectRatio: e.target.value }))}
                />
              </Field>
              <Field label="Width × Height (px)">
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    value={form.width || ''}
                    placeholder="1800"
                    onChange={(e) => setForm((f) => ({ ...f, width: Number(e.target.value) }))}
                  />
                  <span className="text-muted-foreground">×</span>
                  <Input
                    type="number"
                    value={form.height || ''}
                    placeholder="600"
                    onChange={(e) => setForm((f) => ({ ...f, height: Number(e.target.value) }))}
                  />
                </div>
              </Field>
              <Field label="Display (description)" className="sm:col-span-4">
                <Input
                  value={form.display ?? ''}
                  placeholder="Full-width panoramic strip"
                  onChange={(e) => setForm((f) => ({ ...f, display: e.target.value }))}
                />
              </Field>
              <Field label="Shows on (storefront surface)" className="sm:col-span-4">
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.showsOn ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, showsOn: (e.target.value || null) as BannerSurface | null }))
                  }
                >
                  <option value="">General — storefront decides</option>
                  <option value="LISTING_CARD">Listing card — shown on listing / category grids</option>
                  <option value="PRODUCT_PAGE">Product page</option>
                  <option value="CATEGORY">Category / collection page</option>
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Set <span className="font-medium">Listing card</span> for the image the storefront shows on
                  product cards (e.g. a 4:5 portrait).
                </p>
              </Field>
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                {editingId ? 'Save type' : 'Add type'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <Label className="mb-1 block text-[11px]">{label}</Label>
      {children}
    </div>
  );
}

function normalize(form: BannerTypeInput): BannerTypeInput {
  return {
    name: form.name.trim(),
    aspectRatio: form.aspectRatio.trim(),
    width: Math.round(form.width),
    height: Math.round(form.height),
    display: form.display?.trim() ? form.display.trim() : null,
    showsOn: form.showsOn ?? null,
  };
}
